// Brochure.jsx

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import axios from 'axios'
import BannerImg from '../assets/images/banners/Banner1.webp'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import { getBrochureSlug } from '../utils/slugify'
import { ArrowRight, Building, GeoAltFill, Search } from 'react-bootstrap-icons'

// ─── BrochureDots Subcomponent ─────────────────────────────────────────────
function BrochureDots({ count, max }) {
    // If there are no brochures, don't render any dots
    const totalDots = max > 0 ? max : 1
    return (
        <div style={styles.dotsRow}>
            {Array.from({ length: totalDots }).map((_, i) => (
                <span
                    key={i}
                    style={{
                        ...styles.dot,
                        background: i < count ? '#b78b4c' : 'rgba(255,255,255,0.15)',
                    }}
                />
            ))}
        </div>
    )
}

// ─── ProjectCard Subcomponent ──────────────────────────────────────────────
function ProjectCard({ brochure, onClick }) {
    const fallbackBg = 'linear-gradient(140deg, #d6cbb8 0%, #b5a888 100%)'

    // Dynamically calculate brochure count from the files array
    const totalBrochures = brochure?.files?.length || 0

    return (
        <div
            className="luxury-brochure-card"
            style={styles.card}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            aria-label={`View details for ${brochure?.title}`}
        >
            {/* Card image */}
            <div
                style={{
                    ...styles.cardImg,
                    background: brochure?.picture ? `url(${brochure.picture}) center/cover` : fallbackBg,
                }}
            >
                <div style={styles.imgOverlay} />
            </div>

            {/* Card body */}
            <div style={styles.cardBody}>
                {/* Title restricted to exactly 2 max lines with trailing ellipsis */}
                <div style={styles.cardTitle} title={brochure?.title}>
                    {brochure?.title}
                </div>

                {/* 1. Description Block (Max-2 Lines) directly under title */}
                {brochure?.description ? (
                    <div style={styles.cardDescription}>{brochure.description}</div>
                ) : (
                    <div style={styles.cardDescriptionPlaceholder} />
                )}

                {/* 2. Location & Property Type Inline Row (Single Line) */}
                <div style={styles.metaInlineRow}>
                    <div className="align-items-center" style={styles.metaItem}>
                        <div>
                            <GeoAltFill className="text-grey" style={styles.iconSecondary} />
                        </div>
                        <div style={styles.infoText}>{brochure?.location || 'Dubai'}</div>
                    </div>

                    <span className="mx-0" />

                    <div className="align-items-center" style={styles.metaItem}>
                        <div>
                            <Building className="text-grey" style={styles.iconSecondary} />
                        </div>
                        <div style={styles.infoText}>{brochure?.type || 'Apartment'}</div>
                    </div>
                </div>

                {/* 3. Card Footer with Exact-Match Dot Count */}
                <div style={styles.cardFooter}>
                    <div style={styles.brochureInfo}>
                        <BrochureDots count={totalBrochures} max={totalBrochures} />
                        <span style={styles.brochureLabel}>
                            {totalBrochures} brochure{totalBrochures !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div style={styles.viewLink}>
                        View
                        <ArrowRight className="ms-2" />
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Main Brochure Component ───────────────────────────────────────────────
const Brochure = () => {
    const navigate = useNavigate()
    const [brochures, setBrochures] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [contentLoading, setContentLoading] = useState(true)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/brochures/website`)
            .then((res) => {
                if (res.data.brochures && res.data.brochures.length > 0) {
                    setBrochures(res.data.brochures)
                } else {
                    setBrochures([])
                }
            })
            .catch((err) => {
                console.log(err)
            })
            .finally(() => {
                setContentLoading(false)
            })
    }, [])

    // Filter projects based on Title String
    const filteredBrochures = brochures.filter((item) => item?.title?.toLowerCase().includes(searchQuery.toLowerCase()))

    return (
        <div style={styles.page}>
            <style>{globalCSS}</style>

            <Helmet>
                <title> Project Brochures | 800 Homes Dubai </title>
                <meta name="description" content="Browse and download official project brochures, floor plans and payment schedules for top UAE developments." />
            </Helmet>

            <Header />

            {/* Banner Layout */}
            <section
                id="website-responsive"
                className="container-fluid common-banner shadow-800homes"
                style={{ backgroundImage: `url(${BannerImg})` }}
            >
                <div className="row justify-content-center align-items-center">
                    <div className="col-11 col-lg-10">
                        <h1 className="lh-sm fw-600 banner-title text-uppercase text-white text-center">Brochures</h1>
                    </div>
                </div>
            </section>

            {/* Combined Toolbar Header (Count + Search) */}
            <div className="pt-5 mt-5 mb-3 luxury-toolbar-row" style={styles.toolbarContainer}>
                {!contentLoading && (
                    <>
                        <div style={styles.countText}>
                            {filteredBrochures.length} Project{filteredBrochures.length !== 1 ? 's' : ''}
                        </div>
                        <div style={styles.searchWrapper} className="luxury-search-wrapper">
                            <Search style={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by project..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Grid Container */}
            <main style={styles.grid} className="brochures-grid-layout" aria-label="Project brochures layout">
                {contentLoading ? (
                    <div style={styles.loaderWrap}>
                        <Spinner animation="border" variant="white" />
                    </div>
                ) : filteredBrochures.length === 0 ? (
                    <div style={styles.empty}>
                        <p style={styles.emptyTitle}>No Brochures Found</p>
                    </div>
                ) : (
                    filteredBrochures.map((brochure, index) => (
                        <ProjectCard
                            key={brochure._id || brochure.id || index}
                            brochure={brochure}
                            onClick={() => navigate(`/brochure/${getBrochureSlug(brochure)}`)}
                        />
                    ))
                )}
            </main>

            <section className="pt-5 mt-md-5" />

            <Footer />
        </div>
    )
}

// ─── Embedded Global CSS Overrides ──────────────────────────────────────────
const globalCSS = `
    .brochures-grid-container * { box-sizing: border-box; }

    /* Interactive Hover States and Animations */
    .luxury-brochure-card {
        transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease !important;
    }
    
    .luxury-brochure-card:hover {
        transform: translateY(-4px);
        border-color: #b78b4c !important;
        box-shadow: 0 16px 38px rgba(0, 0, 0, 0.45) !important;
    }

    /* Default desktop - 3 columns */
    .brochures-grid-layout {
        display: grid !important;
        grid-template-columns: repeat(3, 1fr) !important;
    }

    /* Toolbar row styling */
    .luxury-toolbar-row {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
    }

    /* Tablet - 3 columns down to 2 */
    @media (max-width: 1024px) {
        .brochures-grid-layout { 
            grid-template-columns: repeat(2, 1fr) !important; 
        }
    }

    /* Large mobile - 2 columns down to 1 */
    @media (max-width: 768px) {
        .brochures-grid-layout { 
            grid-template-columns: 1fr !important; 
        }
        .luxury-toolbar-row { 
            flex-direction: column !important; 
            align-items: stretch !important;
            justify-content: flex-start !important;
            gap: 12px !important;
        }
        .luxury-search-wrapper {
            width: 100% !important;
        }
    }

    /* Mobile - ensure single column */
    @media (max-width: 480px) {
        .brochures-grid-layout { 
            grid-template-columns: 1fr !important; 
        }
    }
`

// ─── Component Scoped Styles ───────────────────────────────────────────────
const styles = {
    page: {
        background: 'transparent',
        minHeight: '100vh',
    },
    toolbarContainer: {
        maxWidth: 1200,
        margin: 'clamp(30px, 5vw, 50px) auto 0',
        padding: '0 clamp(16px, 4vw, 32px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'clamp(12px, 3vw, 32px)',
    },
    countText: {
        fontSize: 'clamp(13px, 2.5vw, 16px)',
        color: '#ccc',
        fontWeight: '400',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
    },
    searchWrapper: {
        position: 'relative',
        width: 'clamp(200px, 100%, 320px)',
        display: 'flex',
        alignItems: 'center',
    },
    searchIcon: {
        position: 'absolute',
        left: '14px',
        color: '#b78b4c',
        fontSize: '16px',
        pointerEvents: 'none',
    },
    searchInput: {
        width: '100%',
        padding: '10px 16px 10px 42px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '6px',
        color: '#fff',
        fontSize: '14px',
        outline: 'none',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        ':focus': {
            borderColor: '#b78b4c',
            background: 'rgba(255, 255, 255, 0.05)',
        },
    },
    grid: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: 'clamp(8px, 2vw, 12px) clamp(16px, 4vw, 32px) clamp(40px, 10vw, 64px)',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'clamp(16px, 3vw, 32px)',
    },
    loaderWrap: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '60px 0',
    },
    empty: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '80px 0',
    },
    emptyTitle: {
        fontSize: 22,
        fontWeight: '600',
        color: '#fff',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.02)',
        overflow: 'hidden',
        cursor: 'pointer',
        outline: 'none',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        flexDirection: 'column',
    },
    cardImg: {
        width: '100%',
        height: 'clamp(160px, 40vw, 220px)',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        padding: 'clamp(8px, 2vw, 12px)',
        overflow: 'hidden',
    },
    imgOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)',
    },
    statusBadge: {
        position: 'relative',
        zIndex: 1,
        fontSize: 10,
        fontWeight: 500,
        padding: '4px 10px',
        borderRadius: 20,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
    },
    badgeOffPlan: {
        background: 'rgba(183,139,76,0.9)',
        color: '#fff',
    },
    badgeReady: {
        background: 'rgba(255,255,255,0.88)',
        color: '#444',
    },
    cardBody: {
        padding: 'clamp(12px, 2.5vw, 16px) clamp(12px, 2.5vw, 16px) clamp(16px, 2.5vw, 20px) clamp(12px, 2.5vw, 16px)',
    },
    cardTitle: {
        fontSize: 'clamp(16px, 3vw, 20px)',
        fontWeight: '500',
        color: '#fff',
        marginBottom: 'clamp(2px, 1vw, 4px)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 'clamp(22px, 2.5vw, 26px)',
        maxHeight: 'clamp(44px, 5vw, 52px)',
    },
    cardDescription: {
        fontSize: 'clamp(13px, 2.5vw, 15px)',
        color: '#cfcfcf',
        lineHeight: 'clamp(18px, 2.5vw, 22px)',
        fontWeight: '300',
        marginTop: 'clamp(4px, 1vw, 6px)',
        marginBottom: 'clamp(12px, 2vw, 16px)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        height: 'clamp(36px, 5vw, 44px)',
    },
    cardDescriptionPlaceholder: {
        height: 'clamp(36px, 5vw, 44px)',
        marginTop: 'clamp(4px, 1vw, 6px)',
        marginBottom: 'clamp(12px, 2vw, 16px)',
    },
    metaInlineRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(6px, 1.5vw, 10px)',
        marginBottom: 'clamp(12px, 2vw, 16px)',
        flexWrap: 'wrap',
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    metaDivider: {
        color: 'rgba(255, 255, 255, 0.3)',
        fontSize: 14,
    },
    infoText: {
        fontSize: 'clamp(11px, 2vw, 13px)',
        color: '#ccc',
        fontWeight: '400',
        lineHeight: '1.2',
    },
    iconSecondary: {
        fontSize: '14px',
        flexShrink: 0,
    },
    cardFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 'clamp(8px, 2vw, 12px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        marginTop: 'auto',
        gap: 'clamp(8px, 2vw, 12px)',
        flexWrap: 'wrap',
    },
    brochureInfo: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '1.2',
        gap: 'clamp(6px, 1.5vw, 8px)',
    },
    dotsRow: {
        display: 'flex',
        paddingBottom: '3px',
        gap: 4,
    },
    dot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        flexShrink: 0,
    },
    brochureLabel: {
        fontSize: 'clamp(11px, 2vw, 13px)',
        color: '#aaa',
    },
    viewLink: {
        fontSize: 'clamp(14px, 2.5vw, 16px)',
        fontWeight: 500,
        color: '#b78b4c',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        whiteSpace: 'nowrap',
    },
}

export default Brochure
